<template lang='pug'>
  .search-view(:class='[focusSearch ? "active" : "nonactive", { "with-offline-banner": isOffline }]' @click.self='closeSearch')
    .search-bar
      form.search-form(action='/search' @submit.prevent='search')
        .input-section
          input.search-input(type='text' autocomplete='off' name='q' :value='query' @keyup='updateQuery' :placeholder='$t("navigation.search.searchMessage")' @focus='focusSearchInput' :aria-label='$t("navigation.search.searchMessage")')
          a.close-search.svg-icon.close2(v-if='!isMobile && focusSearch' @click.prevent='closeSearch')
          a.btn.btn-sm.svg-icon.search-white.search-button(:href='`/search?q=${encodeURIComponent(query)}`' @click.prevent='search')

        template(v-if='isMobile')
          a.close-search.svg-icon.close2(v-if='focusSearch' @click.prevent='closeSearch')
        transition(name='fade')
          search-results(v-if='focusSearch')
</template>

<script>
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import { searchUtil } from '@/mixins/layoutUtil'
import { pushDataLayerEvent } from '@/modules/services/gtm'

const SearchResults = () => import(
  /* webpackChunkName: 'layout-search-results' */ './search-results.vue'
)

export default {
  components: {
    SearchResults
  },

  mixins: [
    searchUtil
  ],

  data () {
    return {
      debounceSearch: debounce(() => {
        pushDataLayerEvent('search_menu_start_input_filling')
        this.fetchResults()
      }, 250)
    }
  },

  computed: {
    ...mapState({
      query: state => state.search.searchQuery,
      focusSearch: state => state.search.focusSearch,
      brands: state => state.brands.allBrands,
      categories: state => state.category.categories,
      isOffline: state => state.layout.isOffline,
      isResultLoaded: state => state.search.isResultLoaded
    }),
    isMobile: () => vueSupport.isMobile
  },

  watch: {
    query (newValue, oldValue) {
      if (
        this.query === undefined ||
        this.query.trim().length < 3 ||
        newValue.trim() === oldValue.trim()
      ) {
        return
      }

      this.$store.commit('setIsSearchingOthers', true)
      this.$store.commit('setIsSearchingProducts', true)
      // Only start searching after user input has a short pause
      this.debounceSearch()
    }
  },

  methods: {
    ...mapActions([
      'addRecentSearch'
    ]),

    updateQuery (e) {
      this.$store.commit('setSearchQuery', e.target.value)
    },

    focusSearchInput () {
      // Checks if a scrollbar exists and sets its width to prevent page jumps when scrollbar is disabled
      Modal.prototype._checkScrollbar() // eslint-disable-line no-undef
      if (!this.focusSearch) {
        // Only add padding for when search is not already focused else padding will be cumulatively added
        Modal.prototype._setScrollbar() // eslint-disable-line no-undef
      }
      this.$store.commit('setFocusSearch', true)

      if (this.query === undefined || this.query.trim().length < 2) { return }

      if (!this.isResultLoaded) {
        this.fetchResults()
      }
    },

    fetchResults () {
      this.$store.dispatch('fetchAlgoliaSearchResults')
    },

    search () {
      const query = this.query.trim()

      if (query.length < 2) { return }

      this.trackSearchSubmission(query)
      this.addRecentSearch({ query })

      window.location.href = `/search?q=${encodeURIComponent(query)}`
    },

    trackSearchSubmission (query) {
      pushDataLayerEvent(
        'search_menu_submit_click', {
          params: {
            search_term: query
          }
        }
      )
    }
  }
}
</script>

<style lang='scss' scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .4s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .input-section {
    width: 100%;
    display: flex;
    flex-direction: row;

    @include media-breakpoint-up(sm) {
      position: relative;
    }
  }

  .search-bar {
    background: $white;

    @include media-breakpoint-up(sm) {
      background: transparent;
      height: 40px;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    }

    @include media-breakpoint-only(lg) {
      width: initial;
    }

    @include media-breakpoint-up(lg) {
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }

  .search-form {
    width: 100%;
    padding: 0;

    @include media-breakpoint-up(lg) {
      width: 400px;
      display: inline-block;
    }

    @include media-breakpoint-up(xl) {
      width: 480px;
    }

    .search-input {
      @include font-weight-book;
      padding-top: 3px;
      padding-left: 20px;
      padding-right: 20px;
      box-shadow: none;
      width: 100%;
      background: $white;
      border: 1px solid $black;

      @include media-breakpoint-up(lg) {
        width: 360px;
      }

      @include media-breakpoint-up(xl) {
        margin: 0;
        width: 100%;
        padding-left: 15px;
        padding-right: 0;
        padding-bottom: 0;
        outline: none;
        color: $black;
        font-size: 14px;
        line-height: 14px;
        box-sizing: border-box;
        -webkit-appearance: none;
      }

      &::-webkit-input-placeholder {
        font-size: 14px;
        line-height: 18px;
        color: $grey-mid;
      }

      &::-moz-placeholder {
        font-size: 14px;
        color: $grey-mid;
      }

      &:-ms-input-placeholder {
        font-size: 14px;
        line-height: 14px;
        color: $grey-mid;
      }

      &::-ms-clear {
        display: none;
      }
    }
  }

  .search-button {
    display: block;
    min-width: 45px;
    width: 45px;
    height: 45px;
    color: $white;
    margin: 0;
    background-size: 15px 15px;

    @include media-breakpoint-up(sm) {
      min-width: 40px;
      width: 40px;
      height: 40px;
    }
  }

  .search-view {
    overflow: hidden;
    width: 100%;
    margin: auto;
    position: relative;
    left: 0;
    background: rgba(255, 255, 255, 0.7);

    @include media-breakpoint-down(sm) {
      margin-top: 11px;
    }

    @include media-breakpoint-up(md) {
      margin-right: 0;
    }

    .close-search {
      position: absolute;
      top: 3px;
      right: 2px;
      height: 41px;
      width: 41px;
      background-color: $white;

      @include media-breakpoint-up(sm) {
        right: 40px;
        top: 3px;
        height: 35px;
        width: 35px;
      }

      &:hover {
        color: inherit;
      }
    }

    &.nonactive {
      @include media-breakpoint-up(sm) {
        height: 40px;
      }
    }

    &.active {
      @include media-breakpoint-down(sm) {
        position: fixed;
        width: 100%;
        top: 65px;
        bottom: 0;
        overflow: hidden;

        &.with-offline-banner {
          top: 65px + $offline-banner-height;
        }

        .search-bar {
          position: relative;
        }

        .search-input {
          width: 100%;
          border-right: 1px solid $black;
          border-bottom: 1px solid $black;
        }

        .search-button {
          display: none;
        }
      }

      @include media-breakpoint-up(md) {
        .input-section {
          z-index: $zindex-modal;
        }
      }
    }
  }
</style>
